// AccountingForm.jsx - Enhanced with autocomplete, delivery linking, groszy conversion
// Fix #37: summary box now includes ALL delivery items (not just unpaid/partial)
// Fix #38: linking now attaches income_id to an EXISTING DeliveryPayment row via PUT,
//          instead of creating a new additive payment row via POST (which double-counted).
// Fix #38 part 1: Income POST body now includes client_id.
// Phase 3 — Multi-line entry: one Type + one Date shared across the whole submission;
//           each row has its own category/client, amount, notes, and (for income) its
//           own optional "link to delivery payment". Rows are submitted one at a time
//           to the existing single-entry endpoints — no backend changes required.

import React, { useState } from "react";
import { apiClient } from "../apiClient";
import {
  plnToGroszy,
  findOrCreateCategory,
  PREDEFINED_EXPENSE_CATEGORIES,
  getTodayISO
} from "./accountingUtils";

// Creates a fresh blank row. Each row gets a stable localId (not a DB id) so React
// can key on it and so we can update/remove individual rows in the array.
let nextLocalId = 1;
const makeBlankRow = () => ({
  localId: nextLocalId++,
  categoryName: "",
  amount: "",
  notes: "",
  linkToDelivery: false,
  selectedPaymentId: "",
  showCategoryDropdown: false,
  filteredCategories: [],
  allDeliveryItems: [],
  linkablePayments: []
});

export default function AccountingForm({ onSaveSuccess }) {
  const [type, setType] = useState("income");
  const [date, setDate] = useState(getTodayISO());
  const [rows, setRows] = useState([makeBlankRow()]);

  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // ⚠️ TODO: Remove this line when accountingUtils.js is fixed to use apiClient
  const token = localStorage.getItem("authToken");

  // ==========================================
  // FETCH CLIENTS (for income sources) — once, on first focus of any category field
  // ==========================================
  const ensureClientsLoaded = async () => {
    if (clientsLoaded || !token) return;
    try {
      const data = await apiClient.get("/clients/");
      const activeClients = data.filter(c => c.is_active === undefined || c.is_active === 1);
      setClients(activeClients);
      setClientsLoaded(true);
    } catch (err) {
      // Removed console.error
    }
  };

  // ==========================================
  // ROW HELPERS
  // ==========================================
  const updateRow = (localId, patch) => {
    setRows(prev => prev.map(r => (r.localId === localId ? { ...r, ...patch } : r)));
  };

  const addRow = () => {
    setRows(prev => [...prev, makeBlankRow()]);
  };

  const removeRow = (localId) => {
    setRows(prev => (prev.length === 1 ? prev : prev.filter(r => r.localId !== localId)));
  };

  // ==========================================
  // FETCH ALL DELIVERY ITEMS + LINKABLE PAYMENTS FOR ONE ROW'S CLIENT
  // (Fix #37 summary box + Fix #38 Q1/Q2 linkable dropdown, now per-row)
  // ==========================================
  const refreshRowDeliveryData = async (localId, clientName) => {
    const client = clients.find(c => c.name === clientName);
    if (!client) {
      updateRow(localId, { allDeliveryItems: [], linkablePayments: [] });
      return;
    }

    try {
      const allItems = await apiClient.get("/delivery-items/");
      const plans = await apiClient.get("/delivery-plans/");

      const clientItems = allItems
        .filter(item => item.client_id === client.id)
        .map(item => {
          const plan = plans.find(p => p.id === item.delivery_plan_id);
          return { ...item, delivery_date: plan ? plan.date : "Unknown" };
        });

      const payments = await apiClient.get(`/delivery-payments/?client_id=${client.id}`);
      const itemsById = new Map(clientItems.map(item => [item.id, item]));

      const eligible = payments
        .filter(p => p.income_id === null || p.income_id === undefined)
        .map(p => ({ ...p, parentItem: itemsById.get(p.delivery_item_id) }))
        .filter(p =>
          p.parentItem &&
          (p.parentItem.payment_status === "Partial Paid" || p.parentItem.payment_status === "Paid")
        );

      updateRow(localId, { allDeliveryItems: clientItems, linkablePayments: eligible });
    } catch (err) {
      // Removed console.error
    }
  };

  // ==========================================
  // AUTOCOMPLETE LOGIC (per row)
  // ==========================================
  const handleCategoryInput = (localId, value) => {
    updateRow(localId, { categoryName: value });

    if (value.trim().length === 0) {
      updateRow(localId, { showCategoryDropdown: false });
      return;
    }

    let suggestions = [];
    if (type === "income") {
      suggestions = clients.map(c => c.name);
      suggestions.push("Other Income");
    } else {
      suggestions = PREDEFINED_EXPENSE_CATEGORIES;
    }

    const filtered = suggestions.filter(s =>
      s.toLowerCase().includes(value.toLowerCase())
    );

    updateRow(localId, { filteredCategories: filtered, showCategoryDropdown: filtered.length > 0 });
  };

  const selectCategory = (localId, categoryName) => {
    updateRow(localId, { categoryName, showCategoryDropdown: false });
    const row = rows.find(r => r.localId === localId);
    if (type === "income" && row?.linkToDelivery) {
      refreshRowDeliveryData(localId, categoryName);
    }
  };

  const toggleLinkToDelivery = (localId, checked) => {
    updateRow(localId, { linkToDelivery: checked, selectedPaymentId: "" });
    if (checked) {
      const row = rows.find(r => r.localId === localId);
      refreshRowDeliveryData(localId, row?.categoryName || "");
    }
  };

  // ==========================================
  // SELECT A PAYMENT TO LINK — auto-fill amount (Fix #38, per row)
  // ==========================================
  const selectPaymentToLink = (localId, paymentId) => {
    const row = rows.find(r => r.localId === localId);
    const payment = row?.linkablePayments.find(p => p.id === parseInt(paymentId));
    updateRow(localId, {
      selectedPaymentId: paymentId,
      amount: payment ? (payment.amount / 100).toFixed(2) : row.amount
    });
  };

  // ==========================================
  // TYPE CHANGE — shared across all rows, reset category names since suggestions differ
  // ==========================================
  const handleTypeChange = (newType) => {
    setType(newType);
    setRows(prev => prev.map(r => ({
      ...r,
      categoryName: "",
      linkToDelivery: false,
      selectedPaymentId: "",
      allDeliveryItems: [],
      linkablePayments: []
    })));
  };

  // ==========================================
  // FORM SUBMISSION — one row at a time, same endpoints as before
  // ==========================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!token) {
      setError("Not logged in");
      return;
    }

    setLoading(true);

    const endpoint = type === "income" ? "/accounting/income" : "/accounting/expenses";
    const failedRows = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      try {
        let selectedPayment = null;

        // 0. If linking, validate the typed amount matches the payment being tagged (Q2)
        if (type === "income" && row.linkToDelivery && row.selectedPaymentId) {
          selectedPayment = row.linkablePayments.find(
            p => p.id === parseInt(row.selectedPaymentId)
          );

          if (selectedPayment) {
            const typedGroszy = plnToGroszy(row.amount);
            if (typedGroszy !== selectedPayment.amount) {
              failedRows.push(
                `Row ${i + 1} (${row.categoryName || "no category"}): amount must match the selected payment exactly (${(selectedPayment.amount / 100).toFixed(2)} PLN).`
              );
              continue;
            }
          }
        }

        // 1. Find or create category
        const categoryId = await findOrCreateCategory(type, row.categoryName, token);

        // 2. Find matching client (for client_id on Income — Fix #38 part 1)
        const matchedClient = clients.find(c => c.name === row.categoryName);

        // 3. Create income/expense entry
        const body = {
          category_id: categoryId,
          amount_groszy: plnToGroszy(row.amount),
          date: date,
          notes: row.notes || null,
          client_id: type === "income" ? (matchedClient?.id ?? null) : undefined
        };

        const createdEntry = await apiClient.post(endpoint, body);

        // 4. If linking, PUT the existing payment (do NOT create a new one — Fix #38)
        if (type === "income" && row.linkToDelivery && selectedPayment) {
          try {
            await apiClient.put(`/delivery-payments/${selectedPayment.id}`, {
              delivery_item_id: selectedPayment.delivery_item_id,
              amount: selectedPayment.amount,
              notes: selectedPayment.notes || null,
              income_id: createdEntry.id,
              payment_method: selectedPayment.payment_method || null
            });
          } catch (deliveryErr) {
            // Removed console.error (non-critical)
          }
        }
      } catch (err) {
        failedRows.push(`Row ${i + 1} (${row.categoryName || "no category"}): ${err.message}`);
      }
    }

    setLoading(false);

    if (failedRows.length > 0) {
      setError(
        rows.length === 1
          ? failedRows[0]
          : `${rows.length - failedRows.length} of ${rows.length} rows saved. Problems: ${failedRows.join(" | ")}`
      );
      // Keep whatever rows the user had — nothing is cleared on partial/total failure,
      // so nothing typed gets lost.
      return;
    }

    // All rows saved successfully
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);

    setDate(getTodayISO());
    setRows([makeBlankRow()]);

    if (onSaveSuccess) onSaveSuccess();
  };

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-[#2D2F5E] border-opacity-20">
      <h2 className="text-lg font-bold text-[#2D2F5E] mb-3">📊 Add Income / Expense</h2>

      {success && (
        <div className="bg-green-100 border border-green-300 text-green-700 p-2 rounded text-sm mb-3">
          ✅ {rows.length > 1 ? "All entries saved successfully!" : "Entry saved successfully!"}
        </div>
      )}

      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 p-2 rounded text-sm mb-3">
          ❌ {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Type Selector — shared across all rows */}
        <div>
          <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Type</label>
          <select
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
          >
            <option value="income">💰 Income</option>
            <option value="expense">💸 Expense</option>
          </select>
        </div>

        {/* Date — shared across all rows */}
        <div>
          <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
            required
          />
        </div>

        {/* Rows */}
        <div className="space-y-3">
          {rows.map((row, index) => {
            // Fix #37: summary now built from ALL items (any payment_status), per row's client
            const clientSummary = row.allDeliveryItems.reduce((acc, item) => {
              const totalValue = item.total_value_groszy || 0;
              const paid = item.payment_amount_groszy || 0;
              if (!acc.totalValue) {
                acc.totalValue = 0;
                acc.totalPaid = 0;
              }
              acc.totalValue += totalValue;
              acc.totalPaid += paid;
              return acc;
            }, {});
            const totalDue = (clientSummary.totalValue || 0) - (clientSummary.totalPaid || 0);

            return (
              <div
                key={row.localId}
                className="p-3 border border-[#2D2F5E] border-opacity-20 rounded-lg bg-[#FAFAF8] relative"
              >
                {rows.length > 1 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-[#2D2F5E] opacity-70">
                      Row {index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeRow(row.localId)}
                      className="text-xs text-red-600 hover:underline"
                    >
                      ✕ Remove
                    </button>
                  </div>
                )}

                {/* Category (Autocomplete) */}
                <div className="relative mb-2">
                  <label className="block text-xs font-medium text-[#2D2F5E] mb-1">
                    {type === "income" ? "Client / Source" : "Category"}
                  </label>
                  <input
                    type="text"
                    value={row.categoryName}
                    onChange={(e) => handleCategoryInput(row.localId, e.target.value)}
                    onFocus={() => {
                      ensureClientsLoaded();
                      handleCategoryInput(row.localId, row.categoryName);
                    }}
                    placeholder={type === "income" ? "Select or type client name" : "Select or type category"}
                    className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                    required
                    autoComplete="off"
                  />

                  {row.showCategoryDropdown && (
                    <div className="absolute z-10 w-full bg-white border border-[#2D2F5E] border-opacity-30 rounded mt-1 max-h-40 overflow-y-auto shadow-lg">
                      {row.filteredCategories.map((cat, idx) => (
                        <div
                          key={idx}
                          onClick={() => selectCategory(row.localId, cat)}
                          className="p-2 hover:bg-[#F5E9D8] cursor-pointer text-[#2D2F5E] text-sm"
                        >
                          {cat}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Amount (in PLN) */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Amount (PLN)</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={row.amount}
                    onChange={(e) => updateRow(row.localId, { amount: e.target.value })}
                    className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                    required
                    min="0.01"
                    step="0.01"
                  />
                  {row.linkToDelivery && row.selectedPaymentId && (
                    <div className="text-xs text-gray-500 mt-1">
                      Must match the selected payment exactly — auto-filled, edit not recommended.
                    </div>
                  )}
                </div>

                {/* Link to Delivery (Income only) */}
                {type === "income" && (
                  <div className="mb-2">
                    <label className="flex items-center gap-2 text-sm text-[#2D2F5E]">
                      <input
                        type="checkbox"
                        checked={row.linkToDelivery}
                        onChange={(e) => toggleLinkToDelivery(row.localId, e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span>Link to existing delivery payment (optional)</span>
                    </label>

                    {row.linkToDelivery && (
                      <div className="mt-2">
                        {row.allDeliveryItems.length > 0 && (
                          <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                            <div className="font-medium text-[#2D2F5E]">{row.categoryName}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              Total Sales: {(clientSummary.totalValue / 100).toFixed(2)} PLN |
                              Paid: {(clientSummary.totalPaid / 100).toFixed(2)} PLN |
                              <span className="font-semibold text-red-600"> Due: {(totalDue / 100).toFixed(2)} PLN</span>
                            </div>
                          </div>
                        )}

                        <select
                          value={row.selectedPaymentId}
                          onChange={(e) => selectPaymentToLink(row.localId, e.target.value)}
                          className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                        >
                          <option value="">Select a payment to tag (optional)</option>
                          {row.linkablePayments.map((payment) => {
                            const dateLabel = payment.paid_at
                              ? new Date(payment.paid_at).toISOString().slice(0, 10)
                              : (payment.parentItem?.delivery_date || "Unknown date");
                            return (
                              <option key={payment.id} value={payment.id}>
                                {dateLabel} — {payment.parentItem?.item_name || "Item"} — {(payment.amount / 100).toFixed(2)} PLN
                              </option>
                            );
                          })}
                          {row.linkablePayments.length === 0 && (
                            <option value="" disabled>No untagged payments for this client</option>
                          )}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-xs font-medium text-[#2D2F5E] mb-1">Notes (optional)</label>
                  <textarea
                    placeholder="Additional details..."
                    value={row.notes}
                    onChange={(e) => updateRow(row.localId, { notes: e.target.value })}
                    className="w-full p-2 border border-[#2D2F5E] border-opacity-30 rounded text-[#2D2F5E] bg-white"
                    rows="2"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Add another row */}
        <button
          type="button"
          onClick={addRow}
          className="w-full border-2 border-dashed border-[#2D2F5E] border-opacity-30 text-[#2D2F5E] px-4 py-2 rounded-lg hover:bg-[#F5E9D8] transition text-sm font-medium"
        >
          ➕ Add another {type === "income" ? "income" : "expense"} line
        </button>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition shadow disabled:opacity-60 font-medium"
        >
          {loading ? "Saving..." : `💾 Save ${rows.length > 1 ? `${rows.length} Entries` : "Entry"}`}
        </button>
      </form>
    </div>
  );
}